'use client';
import { useTranslations } from 'next-intl';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Option } from '../../../../../../form-controls/types';
import type { LotteryName } from '../../../../../../lottery-purchases/types';
import type { Drawing, ExtraGame } from '../../../../../../lottery-results/types';
import { ResultsInteractionContext } from '../../../../context/ResultsInteractionContext';

type ResultData = Pick<Drawing, 'numbers' | 'extraNumbers' | 'extraGames' | 'lotteryId'>;
type UseResultsReturn = {
  lotteryId: LotteryName;
  result: ExtraGame;
  options: Option<string>[];
  selectedOption: Option<string>;
  hasExtraGames: boolean;
  handleOptionChange: (option: Option<string>) => void;
};

/**
 * Hook to get the mini result data in right format together,
 * together with options, selected option and handle option change.
 * For translations namespace is 'lotteries.<lotteryId>' and key is 'name' and 'extra_game_<extraGameName>'.
 *
 * @param data - The drawing data.
 * @returns The mini result data.
 */
function useResults(data: ResultData): UseResultsReturn {
  const { lotteryId, numbers, extraNumbers, extraGames } = data;
  const t = useTranslations(`lotteries.${lotteryId}`);
  const interactionContext = useContext(ResultsInteractionContext);

  const { results, options, mainOption, hasExtraGames } = useMemo(() => {
    const mainResult: ExtraGame = { name: lotteryId, numbers, extraNumbers };
    const mainOption: Option<string> = {
      label: t('name'),
      value: lotteryId,
      showNotification: interactionContext?.shouldShowNotification(lotteryId) ?? false,
    };

    const results: ExtraGame[] = [mainResult];
    const options: Option<string>[] = [mainOption];
    const hasExtraGames = extraGames && extraGames.length > 0;
    if (hasExtraGames) {
      extraGames.forEach(game => {
        results.push(game);
        options.push({
          label: t(`extra_game_${game.name}`),
          value: game.name,
          showNotification: interactionContext?.shouldShowNotification(game.name) ?? false,
        });
      });
    }
    return { results, options, mainOption, hasExtraGames };
  }, [lotteryId, numbers, extraNumbers, extraGames, t, interactionContext]);

  const [localSelectedOption, setLocalSelectedOption] = useState<Option<string>>(mainOption);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (interactionContext?.setSelectedOption && !interactionContext.selectedOption && !hasInitialized.current) {
      interactionContext.setSelectedOption(mainOption);
      hasInitialized.current = true;
    }
  }, [interactionContext, mainOption]);

  const selectedOption = interactionContext?.selectedOption ?? localSelectedOption;
  const setSelectedOption = interactionContext?.setSelectedOption ?? setLocalSelectedOption;

  const result = results.find(result => result.name === selectedOption.value)!;

  const handleOptionChange = (option: Option<string>) => {
    setSelectedOption(option);
  };

  return { lotteryId, result, options, selectedOption, hasExtraGames: !!hasExtraGames, handleOptionChange };
}

export { useResults };
export type { ResultData, UseResultsReturn };
